// BillDetailsModal.jsx
import React from 'react';
import OrderBillSender from './OrderBillSender';

const BillDetailsModal = ({ order, restaurant, onClose }) => {
    if (!order) return null;

    const baseTotal = order.totalAmount ?? (order.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0);
    const serviceAmount = order.serviceAmount && order.serviceAmount > 0 ? order.serviceAmount : 0;
    const offerDiscount = order.offerDiscount && order.offerDiscount > 0 ? order.offerDiscount : 0;
    const restaurantName = restaurant?.name || 'Restaurant';
    const restaurantLogo = restaurant?.logo;
    const gstNo = restaurant?.GST || restaurant?.gstNumber || '';

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Modal chrome: title + close */}
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-stone-200 flex-shrink-0 bg-stone-50/50">
                    <h2 className="text-base sm:text-lg font-bold text-stone-800">Bill</h2>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-4 sm:p-5 space-y-4">
                    {/* Bill structure: restaurant header */}
                    <div className="text-center pb-4 border-b-2 border-orange-400">
                        {restaurantLogo ? (
                            <img
                                src={restaurantLogo}
                                alt={restaurantName}
                                className="h-14 w-14 sm:h-16 sm:w-16 mx-auto rounded-xl object-contain mb-3 border border-stone-200"
                            />
                        ) : (
                            <div className="h-14 w-14 sm:h-16 sm:w-16 mx-auto rounded-xl bg-orange-100 flex items-center justify-center text-2xl sm:text-3xl mb-3 border border-orange-200">
                                🍽️
                            </div>
                        )}
                        <h3 className="text-lg sm:text-xl font-bold text-stone-800">{restaurantName}</h3>
                        {gstNo && (
                            <p className="text-xs text-stone-500 mt-1">GST No: {gstNo}</p>
                        )}
                    </div>

                    {/* Bill label + date */}
                    <div className="flex justify-between items-center text-sm">
                        <span className="font-medium text-stone-700">Invoice / Bill</span>
                        <span className="text-stone-500">
                            {order.orderDate ? new Date(order.orderDate).toLocaleString() : '—'}
                        </span>
                    </div>

                    {/* Customer */}
                    <div className="bg-stone-50 rounded-xl border border-stone-100 p-3">
                        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2">Customer</p>
                        <p className="font-semibold text-stone-800">{order.customerName || '—'}</p>
                        {order.mobileNumber && (
                            <p className="text-sm text-stone-600 mt-0.5">{order.mobileNumber}</p>
                        )}
                        {order.message && (
                            <p className="text-sm text-stone-600 mt-2 italic">&quot;{order.message}&quot;</p>
                        )}
                    </div>

                    {/* Items */}
                    <div>
                        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2">Items</p>
                        <ul className="border border-stone-200 rounded-xl overflow-hidden divide-y divide-stone-100">
                            {order.items?.map((item, idx) => (
                                <li key={idx} className="flex justify-between items-center px-3 py-2.5 bg-white text-sm">
                                    <span className="text-stone-800">
                                        {item.name} <span className="text-stone-500">×{item.quantity}</span>
                                    </span>
                                    <span className="font-semibold text-stone-800">₹{(item.price * item.quantity).toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Summary */}
                    <div className="border border-stone-200 rounded-xl p-3 space-y-2">
                        <div className="flex justify-between text-sm text-stone-600">
                            <span>Subtotal</span>
                            <span className="font-medium text-stone-800">₹{Number(baseTotal).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-stone-600">
                            <span>Tax ({order.taxPercentage ?? 0}%)</span>
                            <span className="font-medium text-stone-800">₹{(order.taxAmount || 0).toFixed(2)}</span>
                        </div>
                        {serviceAmount > 0 && (
                            <div className="flex justify-between text-sm text-stone-600">
                                <span>Service</span>
                                <span className="font-medium text-stone-800">₹{serviceAmount.toFixed(2)}</span>
                            </div>
                        )}
                        {offerDiscount > 0 && (
                            <div className="flex justify-between text-sm text-stone-600">
                                <span>Discount</span>
                                <span className="font-medium text-emerald-600">−₹{offerDiscount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200">
                            <span className="text-stone-800">Total</span>
                            <span className="text-orange-600">₹{(order.finalAmount || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Thank you message */}
                    <div className="text-center py-4 px-3 bg-amber-50/80 border border-amber-200/80 rounded-xl">
                        <p className="text-sm font-medium text-stone-700">Thank you for dining with us.</p>
                        <p className="text-xs text-stone-500 mt-1">We hope to see you again soon.</p>
                    </div>

                    <OrderBillSender orderId={order._id} order={order} showFeedback={false} />
                </div>

                {/* Footer */}
                <div className="p-4 sm:p-5 border-t border-stone-200 flex-shrink-0 bg-stone-50/50">
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold bg-stone-200 text-stone-700 hover:bg-stone-300 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BillDetailsModal;
